"use client";

import clsx from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState, type JSX } from "react";

import { DesktopNav } from "./DesktopNav";
import { Flyout } from "./Flyout";
import { MobileSearch } from "./mobile/MobileSearch";
import { MobileSidebar } from "./mobile/MobileSidebar";
import { MobileTopBar } from "./mobile/MobileTopBar";
import type { ActiveFlyout, SectionKey } from "./Sections";
import "./styles.css";
import { useMediaQuery } from "@/hooks/use-media-query";

interface Props {
  short?: boolean;
  className?: string;
}

/** Tempo (ms) antes de fechar o flyout ao tirar o mouse do header, evitando fechar por trajetos diagonais. */
const FLYOUT_CLOSE_DELAY_MS = 150;

/**
 * Header global do marketplace. Concentra o estado de navegação:
 * - `activeFlyout`: qual painel está aberto (busca, uma seção ou nenhum) — desktop e mobile.
 * - `isSidebarOpen`: menu lateral mobile (independente dos demais).
 * - `expandedTopicId`: tópico aberto no accordion da seção, dentro do menu mobile.
 * A prop `short` não altera mais a navbar (ela é a mesma em todas as páginas).
 */
export const Header = ({ className }: Props): JSX.Element => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const category = searchParams.get("category");
  const type = searchParams.get("type");
  const order = searchParams.get("order");

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFlyout, setActiveFlyout] = useState<ActiveFlyout>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const router = useRouter();
  const headerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    setActiveFlyout(null);
    setIsSidebarOpen(false);
  }, [isDesktop]);

  function onSearch() {
    const trimmedTerm = searchTerm.trim();

    if (trimmedTerm.length > 0) {
      closeAllPanels();
      router.push(`/busca?search=${encodeURIComponent(trimmedTerm)}`);
    }
  }

  function closeAllPanels() {
    setActiveFlyout(null);
    setIsSidebarOpen(false);
    setExpandedTopicId(null);
  }

  function cancelScheduledClose() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }

  /** Fecha seções ao sair do header com o mouse; a busca só fecha por ação explícita (×, Esc, clique fora). */
  function scheduleClose() {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      setActiveFlyout((current) => (current === "busca" ? current : null));
    }, FLYOUT_CLOSE_DELAY_MS);
  }

  function openSidebar() {
    setActiveFlyout(null);
    setExpandedTopicId(null);
    setIsSidebarOpen(true);
  }

  /** Mobile: entra na visão de uma seção dentro do menu lateral. */
  function selectSidebarSection(sectionKey: SectionKey) {
    setExpandedTopicId(null);
    setActiveFlyout(sectionKey);
  }

  function backToSidebarCategories() {
    setExpandedTopicId(null);
    setActiveFlyout(null);
  }

  /** Mobile: abre a busca em tela cheia (fecha o menu lateral, se aberto). */
  function openMobileSearch() {
    setIsSidebarOpen(false);
    setExpandedTopicId(null);
    setActiveFlyout("busca");
  }

  function toggleTopic(topicId: string) {
    setExpandedTopicId((current) => (current === topicId ? null : topicId));
  }

  // Fecha tudo ao navegar (rota ou filtros mudaram)
  useEffect(() => {
    closeAllPanels();
  }, [pathname, category, type, order]);

  // Fecha ao clicar fora do header
  useEffect(() => {
    if (!activeFlyout) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveFlyout(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeFlyout]);

  // Menu lateral mobile: Esc fecha e a página atrás não rola
  useEffect(() => {
    if (!isSidebarOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAllPanels();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSidebarOpen]);

  useEffect(() => cancelScheduledClose, []);

  return (
    <div
      ref={headerRef}
      className={clsx(className, "mb-1 shadow-[5px_22px_20px_-30px_#556A7540] lg:mb-5 lg:shadow-none")}
    >
      <div className="system-header lg:flex lg:flex-col lg:items-center">
        {/* Desktop */}
        <div
          className="relative hidden w-full max-w-page-max-width lg:block"
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        >
          <DesktopNav
            activeFlyout={activeFlyout}
            onFlyoutChange={setActiveFlyout}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onSearch={onSearch}
          />

          <Flyout activeFlyout={activeFlyout} />
        </div>

        {/* Mobile */}
        <MobileTopBar onOpenSidebar={openSidebar} onOpenSearch={openMobileSearch} />

        <MobileSidebar
          isOpen={isSidebarOpen}
          activeFlyout={activeFlyout}
          expandedTopicId={expandedTopicId}
          onClose={closeAllPanels}
          onSelectSection={selectSidebarSection}
          onBack={backToSidebarCategories}
          onToggleTopic={toggleTopic}
          onOpenSearch={openMobileSearch}
        />

        <MobileSearch
          isOpen={activeFlyout === "busca"}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSearch={onSearch}
          onClose={() => setActiveFlyout(null)}
        />
      </div>
    </div>
  );
};
